import { CALCULATION_TYPE } from "@erp/shared/components/ui/Document/_types/calculation-type.type";
import { findRow, setCalcCostRow, setUnitPricesByFormula, updateAllTotals, } from "@erp/shared/components/ui/Document/_utils/calc-cost-row";
import { defaultCalcCostDetailData, } from "@erp/shared/components/ui/Document/CalcCost/const";
import { useDirectInputCost } from "@erp/shared/components/ui/Document/CalcCost/DirectInputCostTable/useDirectInputCost";
import { computed, ref, watch } from "vue";
import { cloneDeep } from "es-toolkit";
import { useQuery } from "@tanstack/vue-query";
import { useState } from "@erp/shared/composables/useState";
import { queries } from "@erp/shared/queries";
export const useCalcCost = () => {
    const calcStockCosts = ref([]);
    const calcConstructionMaterialCosts = ref([]);
    const calcLaborCosts = ref([]);
    const laborCosts = ref([]);
    const directInputCost = useDirectInputCost();
    const radioOptions = [
        { label: CALCULATION_TYPE.MATERIAL, value: CALCULATION_TYPE.MATERIAL },
        { label: CALCULATION_TYPE.SERVICE, value: CALCULATION_TYPE.SERVICE },
    ];
    const [calculationType, setCalculationType] = useState(CALCULATION_TYPE.MATERIAL);
    const calcCostDetailData = ref(cloneDeep(defaultCalcCostDetailData));
    const totalPrice = computed(() => {
        return calcCostDetailData.value.reduce((acc, curr) => {
            if (curr.hidden)
                return acc;
            return acc + getCostTotal(curr);
        }, 0);
    });
    const materialCostRow = computed(() => findRow(calcCostDetailData.value, "재료비"));
    const laborCostRow = computed(() => findRow(calcCostDetailData.value, "노무비"));
    const expenseCostRow = computed(() => findRow(calcCostDetailData.value, "경비"));
    const mgmtCostRow = computed(() => findRow(calcCostDetailData.value, "일반관리비"));
    const profitCostRow = computed(() => findRow(calcCostDetailData.value, "이윤"));
    const { data: outputRatios } = useQuery(queries.laborCost.outputRatio());
    /**
     * @description cost_total을 숫자로 안전하게 변환
     */
    const getCostTotal = (row) => {
        return Number(row?.cost_total) || 0;
    };
    const getBaseValue = (costList) => {
        switch (costList) {
            case "간접 노무비":
            case "공구손료":
            case "퇴직공제 부금비":
            case "산업안전보건관리비":
                return getCostTotal(findRow(laborCostRow.value?.belong_data ?? [], "직접 노무비"));
            case "고용보험료":
            case "여비교통 통신비":
            case "산재보험료":
            case "세금과공과":
            case "복리후생비":
            case "소모품비":
                return getCostTotal(laborCostRow.value);
            case "일반관리비":
                return (getCostTotal(materialCostRow.value) +
                    getCostTotal(laborCostRow.value) +
                    getCostTotal(expenseCostRow.value));
            case "이윤":
                return (getCostTotal(laborCostRow.value) +
                    getCostTotal(expenseCostRow.value) +
                    getCostTotal(mgmtCostRow.value));
            default:
                return 0;
        }
    };
    const deleteCalcStockCost = (productCode) => {
        calcStockCosts.value = calcStockCosts.value.filter((i) => i.product_code !== productCode);
    };
    const mergeCalcStockCost = (list) => {
        if (!list || list.length === 0) {
            calcStockCosts.value = [];
            return;
        }
        const newItems = list.map((stock) => ({
            id: stock.id,
            product_code: stock.productCode,
            cost_list: stock.name,
            cost_list_sub: stock.spec ?? undefined,
            cost_unit: "식",
            cost_num: 1,
            cost_unit_price: Number(stock.unitPrice) || 0,
            cost_total: 0,
            deletable_row: true,
            cost_num_editable: true,
            cost_unit_price_editable: true,
            allow_one_or_greater: true,
        }));
        // immer 없이 직접 할당
        calcStockCosts.value = newItems;
    };
    /** 노무비 목록 병합 (소수점 유지, 정수) */
    const mergeLaborCosts = (list) => {
        laborCosts.value = cloneDeep(list);
        const map = new Map();
        list.forEach((labor) => {
            const mergedUnitPrice = Math.round(labor.unitPrice * labor.quantity * labor.manPerHour);
            const existing = map.get(labor.name);
            if (existing) {
                existing.cost_unit_price += mergedUnitPrice;
            }
            else {
                map.set(labor.name, {
                    cost_list: labor.name,
                    cost_unit: "식",
                    cost_num: 1,
                    cost_unit_price: mergedUnitPrice,
                    cost_total: 0,
                });
            }
        });
        calcLaborCosts.value = [...map.values()];
    };
    const mergeConstructionMaterialCosts = (list) => {
        calcConstructionMaterialCosts.value = cloneDeep(list);
    };
    const resetBeforeApply = () => {
        laborCosts.value = [];
        calcConstructionMaterialCosts.value = [];
        calcCostDetailData.value = cloneDeep(defaultCalcCostDetailData);
    };
    const parseMaterialCalcCost = (calc, labor) => {
        calcCostDetailData.value = cloneDeep(calc);
        const clonedMaterialRow = findRow(calcCostDetailData.value, "재료비");
        const clonedLaborRow = findRow(calcCostDetailData.value, "노무비");
        calcStockCosts.value = cloneDeep(findRow(clonedMaterialRow?.belong_data ?? [], "재료")?.belong_data ?? []);
        calcConstructionMaterialCosts.value = cloneDeep(findRow(clonedMaterialRow?.belong_data ?? [], "공사 자재")?.belong_data ?? []);
        calcLaborCosts.value = cloneDeep(findRow(clonedLaborRow?.belong_data ?? [], "직접 노무비")?.belong_data ?? []);
        laborCosts.value = cloneDeep(labor);
        recalculate();
    };
    /**
     * @description 견적서·수주서 재료·공사 자재·노무비 공통 적용
     * @param data 견적/수주서 상세 정보 (둘 다 `calc` 와 `labor` 프로퍼티를 가짐)
     */
    const applyCalcCost = (data) => {
        if (!data.calc || !data.info) {
            return;
        }
        resetBeforeApply();
        if (data.info.writeType === 0) {
            setCalculationType(CALCULATION_TYPE.MATERIAL);
            parseMaterialCalcCost(data.calc, data.labor);
        }
        else {
            setCalculationType(CALCULATION_TYPE.SERVICE);
            directInputCost.applyDirectInputCost(data.calc, data.info.totalPrice);
        }
    };
    const recalculate = () => {
        const rows = [laborCostRow, expenseCostRow, mgmtCostRow, profitCostRow];
        rows.forEach((r) => {
            if (r.value && outputRatios.value)
                setUnitPricesByFormula(r.value, outputRatios.value, getBaseValue);
        });
        updateAllTotals(calcCostDetailData.value);
    };
    // NOTE: 재료·공사 자재 변경 시 행 세팅
    watch([calcStockCosts, calcConstructionMaterialCosts], () => {
        setCalcCostRow(materialCostRow.value, "재료", cloneDeep(calcStockCosts.value));
        setCalcCostRow(materialCostRow.value, "공사 자재", cloneDeep(calcConstructionMaterialCosts.value));
        recalculate();
    }, { immediate: true });
    // NOTE: 노무비 변경 시 행 세팅
    watch(calcLaborCosts, () => {
        setCalcCostRow(laborCostRow.value, "직접 노무비", cloneDeep(calcLaborCosts.value));
        recalculate();
    });
    // NOTE: outputRatios 변경 시 재계산
    watch(() => outputRatios.value, () => recalculate());
    return {
        ...directInputCost,
        radioOptions,
        calculationType,
        calcCostDetailData,
        calcConstructionMaterialCosts,
        calcStockCosts,
        calcLaborCosts,
        laborCosts,
        setCalculationType,
        mergeCalcStockCost,
        mergeConstructionMaterialCosts,
        mergeLaborCosts,
        applyCalcCost,
        deleteCalcStockCost,
        totalPrice,
        recalculate,
    };
};
